import {
  ChangeDetectorRef,
  Component,
  HostListener,
  TemplateRef,
  viewChild,
  inject,
  DOCUMENT
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  GridColumnV2,
  GridStateChangeV2,
  ModalService,
  ReactiveComponent,
  TwoButtonModalV2,
  TwoButtonModalV2Component,
  unique,
  ToastV2Service,
  BackendResponse,
  SpinnerV2Service,
  ToolingVerificationLevelPipe,
  ControlButton,
  UserRightsService,
  NotificationsV2Module,
  ButtonV2Module,
  RadioV2Module,
  GridV2Module,
  AssetsModule,
  ServiceErrorV2Component,
} from '@schenck/rotec-components';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { StatusFilterComponent } from 'src/components/status-filter/status-filter.component';
import { createSchedulingFilter } from 'src/components/task-interval.pipe';
import { addTitleAndWidthToGridColumn, PartialColumn } from 'src/pages/config-utils';
import { routes } from 'src/routes';
import { DownloadService, ToolingTaskService } from 'src/services';
import { ChooseToolingTaskOptionModalComponent } from '../choose-tooling-task-option-modal/choose-tooling-task-option-modal.component';
import { ToolingTaskModalComponent } from '../tooling-task-modal/tooling-task-modal.component';
import { ToolingTaskSearchService } from '../tooling-task-search.service';
import { toolingTaskColumnsBaseConfig } from '../tooling-task-settings.api';
import { ToolingTaskSettingsService } from '../tooling-task-settings.service';
import { ToolingTask } from '../tooling-tasks.model';
import { AsyncPipe } from '@angular/common';
import { UserRightsActions } from 'src/utils/enums/user-rights.enum';
import { UpsertToolingTaskWizardComponent } from '../upsert-tooling-task-wizard/upsert-tooling-task-wizard.component';
import { UserRole } from 'src/utils/enums/user-roles.enum';
import { PriorityComponent } from '../../../components/priority/priority.component';
import { DateComponent } from '../../../components/date/date.component';
import { StatusComponent } from '../../../components/status/status.component';
import { StatusFilterComponent as StatusFilterComponent_1 } from '../../../components/status-filter/status-filter.component';
import { TaskIntervalPipe } from '../../../components/task-interval.pipe';
import { TypeEnumPipe } from '../../../components/type-enum.pipe';

@Component({
  selector: 'ma-tooling-tasks',
  templateUrl: './tooling-tasks.component.html',
  styleUrls: ['./tooling-tasks.component.less'],
  imports: [
    NotificationsV2Module,
    TranslatePipe,
    ButtonV2Module,
    RadioV2Module,
    GridV2Module,
    PriorityComponent,
    DateComponent,
    StatusComponent,
    StatusFilterComponent_1,
    AsyncPipe,
    TaskIntervalPipe,
    TypeEnumPipe,
    AssetsModule,
    ServiceErrorV2Component,
  ],
})
export class ToolingTasksComponent extends ReactiveComponent {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(ModalService);
  private readonly downloadService = inject(DownloadService);
  private readonly router = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly taskService = inject(ToolingTaskService);
  private readonly toastService = inject(ToastV2Service);
  private readonly spinnerService = inject(SpinnerV2Service);
  public settingsService = inject(ToolingTaskSettingsService);
  public userRightsService = inject(UserRightsService);
  public searchService = inject(ToolingTaskSearchService);

  readonly statusFilter = viewChild<TemplateRef<StatusFilterComponent>>('statusFilter');
  gridHeight = 500;
  hasPagination: boolean = false;
  gridScrollContainer = this.document;
  public selectedElement: string;
  public actions = UserRightsActions;

  state = this.connect(
    {
      searchData: this.searchService.state$,
      columns: this.onInit$.pipe(switchMap(() => this.createColumns())),
      responsibleCompanyFilterValue: this.searchService.responsibleCompanyFilterValue$,
    },
    this.cdr
  );

  public controlButtons: ControlButton[] = [
    {
      icon: 'trash',
      click: (_) => {
        this.deleteTasks();
      },
    },
    {
      icon: 'file-pdf',
      label: 'COMMON_Export',
      click: (_) => {
        this.export();
      },
    },
  ];

  constructor() {
    super();
    this.searchForToolingIfInRouteParams();
    this.userRightsService
      .hasActionRights$(UserRightsActions.deleteTask)
      .pipe(
        takeUntil(this.onDestroy$),
        filter((actions) => typeof actions === 'boolean')
      )
      .subscribe((hasDeleteRights) => {
        this.controlButtons = this.controlButtons.filter(({ icon }) => {
          switch (icon) {
            case 'trash':
              return hasDeleteRights;
            default:
              return true;
          }
        });
      });
  }

  public ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setGridHeight();

    this.searchService.state$.pipe(takeUntil(this.onDestroy$)).subscribe((state) => {
      this.hasPagination = state.total > state.pageSize;
      this.setGridHeight();
    });
  }

  @HostListener('window:resize') public setGridHeight() {
    const header = this.document.querySelector('.header-row');
    const titleHeight = 75;
    const navbarHeight = this.document.querySelector('.pages-navbar')?.clientHeight;
    const paginationSize = this.hasPagination ? 80 : 0;
    this.gridHeight =
      window.innerHeight - titleHeight - navbarHeight - header.clientHeight - 250 - paginationSize;
  }

  public refineSearch(state: GridStateChangeV2) {
    this.searchService.search({
      filter: <any>state.filter,
      fulltext: state.fulltext,
      page: state.page,
      sort: <any>state.sort,
    });
  }

  public reactToPageSizeChange(pageSize: number) {
    this.searchService.search({ pageSize });
  }

  public keyupEnterSearch(searchText: string) {
    this.searchService.search({ fulltext: searchText, page: 0 });
  }

  public updateColumnSettings(columns: GridColumnV2[]) {
    this.settingsService.updateColumnSettings(columns);
  }

  public openTask(task: any) {
    this.selectedElement = this.selectedElement === task.id ? '' : task.id;
    this.modalService.open<string>(ToolingTaskModalComponent, task.id);
  }

  public toggleSelectedItem($event) {
    this.selectedElement = $event?.id;
  }

  public addNewTask() {
    this.userRightsService.userRole$.pipe(takeUntil(this.onDestroy$)).subscribe((roleName) => {
      roleName === UserRole.Supervisor
        ? this.modalService.open(UpsertToolingTaskWizardComponent)
        : this.modalService.open(ChooseToolingTaskOptionModalComponent);
    });
  }

  public export() {
    this.downloadService.downloadNonExecutedToolingTaskPDF(this.state.searchData.selectedIds);
  }

  public deleteTasks(): void {
    this.modalService
      .open<TwoButtonModalV2, boolean>(TwoButtonModalV2Component, {
        confirmText: 'Delete',
        cancelText: 'Cancel',
        title: '',
        text: 'The following tasks (series) will be deleted: VALUE',
        textParams: {
          value: unique(this.searchService.getSelectedResults().map((res) => res.title)).join(', '),
        },
        summary: 'Delete Tasks',
        darkMode: true,
        warning: true,
      })
      .events$.pipe(
        takeUntil(this.onDestroy$),
        filter((wantsToDelete) => !!wantsToDelete),
        tap(() => this.spinnerService.setLoading(this.searchService.getSettingsKey(), true)),
        switchMap(() => this.taskService.deleteMultiple$(this.searchService.state.selectedIds))
      )
      .subscribe((responses) => {
        this.searchService.setSelectedResults([]);
        if (
          responses.some(
            (response) =>
              !BackendResponse.isSuccess(response) && !BackendResponse.isNotFound(response)
          )
        ) {
          this.toastService.open({
            type: 'error',
            text: 'TECHNICAL_ERROR_try again later or contact admin',
          });
        }
      });
  }

  public isSelected(id: string): boolean {
    return this.state.searchData.selectedIds.includes(id) || this.selectedElement === id;
  }

  private searchForToolingIfInRouteParams(): void {
    const toolingName = this.router.snapshot.queryParamMap.get(routes.toolingTasks.params.tooling);
    if (toolingName) {
      this.searchService.search({ filter: [{ key: 'toolingName', value: [toolingName] }] });
    }
  }

  private createColumns() {
    const columnDefs: PartialColumn[] = [
      {
        key: 'toolingName',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('toolingName') },
      },
      {
        key: 'manufacturer',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('manufacturer') },
      },
      {
        key: 'inventoryNo',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'serial',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'schenckMatNo',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'verificationLevel',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('verificationLevel', (value) => {
            return ToolingVerificationLevelPipe.translate(value, this.translate);
          }),
        },
      },
      {
        key: 'schenckId',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'customerId',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'title',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'priority',
        sortable: true,
        filter: {
          type: 'checkbox',
          showAllCheckbox: true,
          options: [
            { name: 'High', value: 'High' },
            { name: 'Medium', value: 'Medium' },
            { name: 'Low', value: 'Low' },
          ],
        },
      },
      {
        key: 'responsibleCompany',
        sortable: true,
        filter: {
          type: 'radio',
          options: [
            { value: 'Customer', name: 'My' },
            { value: 'Schenck', name: 'Schenck' },
          ],
          displayAllOption: true,
        },
      },
      {
        key: 'responsible',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'executive',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'dueDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'status',
        sortable: true,
        filter: {
          type: 'template',
          template: this.statusFilter(),
          displayValueFn: StatusFilterComponent.filterforStatus,
          deleteFilterFn: StatusFilterComponent.deleteStatusFilter,
        },
      },
      {
        key: 'description',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'type',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('type'), withCustomValue: false },
      },
      { key: 'expectedDurationMinutes', sortable: true },
      {
        key: 'scheduling',
        sortable: true,
        filter: createSchedulingFilter(this.searchService.allResults$, this.translate),
      },
      {
        key: 'machines',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'toolingForRotor',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'mainContact',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'storageLocation',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'workplace',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
    ];

    return this.settingsService.createColumns$(
      addTitleAndWidthToGridColumn(columnDefs, toolingTaskColumnsBaseConfig)
    );
  }

  private getItemSource(entity: keyof ToolingTask, translateFn?: (value: any) => string) {
    return this.searchService.getMultiselectItemSource(entity, translateFn);
  }
}
